/**
 * mentionChip — a mention that RESOLVED is shown as one, so the platform
 * confirms the spelling landed (Miles, 2026-09-21).
 *
 * His ask, verbatim: "The correctly mapped mention should turn into a special
 * gold / special element modern full name of the individual getting mentioned
 * with their icon letter as is common on the platform so the platform confirms
 * it's the correct mention handle."
 *
 * So: `@justin` becomes the person — their glyph and their FULL NAME, in the
 * platform's gold. And an `@handle` that matches nobody stays exactly as it was
 * typed, plain. **That contrast is the confirmation.** Decorating every handle
 * — which is what the one existing `.mention` producer in the tree does, on a
 * surface this transcript never renders — would tell a reader nothing, because
 * the mention that silently reaches nobody would look identical to the one that
 * works.
 *
 * ONE GRAMMAR, THREE SURFACES (Miles, 2026-09-22 — "when a mention happens in
 * the draft box the same thing must happen"). `splitMentionParts` is the rule;
 * everything that shows a mention reads it:
 *   · the message body, as html (`decorateMentionChips`, below);
 *   · the reply quote, as React nodes (QuotedReply — a text node, never a
 *     dangerouslySetInnerHTML sink, so nothing there needs sanitizing);
 *   · the composer, as measured pill geometry (composerMentionPills).
 * A second grammar is how two of them start disagreeing about what a mention
 * is, which is the failure the `PLAIN_MENTION_SOURCE` note already names.
 *
 * WHY THE HTML PASS RUNS ON RENDERED HTML rather than inside the markdown
 * pipeline: a person's own bubble and Prest0n's go through DIFFERENT converters
 * (`autoLinkText` and `markdownToHtml`), and a mention must look the same in
 * both. One pass over the finished html covers them with one rule.
 *
 * It is tag-aware rather than a blunt `String.replace`: the html is split into
 * tags and text, and only TEXT is transformed. A handle inside an `href` is
 * never touched, and a chip can never be minted inside an attribute. The
 * sanitizer that ran before this guarantees the shape that makes the split
 * safe (no raw `<` inside an attribute value), and `span` + `class` are on its
 * allowlist, so a chip minted here survives every later pass.
 */
import { PLAIN_MENTION_SOURCE } from './mentionResolver';

/** The person a handle resolved to, as the chip shows them. */
export interface MentionChipPerson {
  /** The name a reader knows them by — never a uid, never a handle. */
  name: string;
  /** Their avatar letter, the same one every other surface draws. */
  glyph: string;
}

export const MENTION_CHIP_CLASS = 'mention-chip';
export const MENTION_CHIP_GLYPH_CLASS = 'mention-chip-glyph';
/**
 * The letter inside the circle, in a span of its own (2026-09-22) so it can
 * be centred on its INK — the circle is a flex box, and a bare text node there
 * is centred by the font's whole ascent and descent, which a capital never
 * fills. See `.mention-chip-initial` in styles.ts. When these circles become
 * profile photos, the photo takes this span's place.
 */
export const MENTION_CHIP_INITIAL_CLASS = 'mention-chip-initial';

/** The circle and its letter — one shape, shared by both chip producers. */
export function mentionChipGlyphHtml(escapedGlyph: string): string {
  return `<span class="${MENTION_CHIP_GLYPH_CLASS}">`
    + `<span class="${MENTION_CHIP_INITIAL_CLASS}">${escapedGlyph}</span></span>`;
}

/** A stretch of ordinary text between mentions. */
export interface MentionTextPart {
  kind: 'text';
  /** Verbatim source text. */
  text: string;
}

/** A handle that resolved to somebody. */
export interface MentionPersonPart {
  kind: 'mention';
  /** The handle as captured, without the `@` and without trailing `.`/`_`. */
  handle: string;
  /**
   * The exact source text this part stands for, `@` included — so the parts
   * can be laid back down character for character. The composer's pills and
   * the draft's own text are aligned by that property, not by a shared font
   * stack, so nothing can drift.
   */
  raw: string;
  person: MentionChipPerson;
}

export type MentionPart = MentionTextPart | MentionPersonPart;

/**
 * Split text into ordinary stretches and resolved mentions.
 *
 * THE INVARIANT: the parts concatenate back to the input exactly —
 * `parts.map(p => p.kind === 'text' ? p.text : p.raw).join('') === input`.
 * Every consumer depends on it, and the composer's overlay depends on it for
 * geometry, so it is pinned directly.
 *
 * `resolve` returns null for a handle that matches nobody, for `@team`, and for
 * anything the caller declines — and every one of those is left as typed.
 *
 * A TRAILING `.` or `_` IS PUNCTUATION, not part of the name. The grammar
 * admits both inside a handle (`@justin.zelaya` is one identifier), so "@Prest0n."
 * at the end of a sentence captured `Prest0n.` and resolved to nobody — a
 * mention that reaches its target and is drawn as though it had missed. The
 * summon gate has stripped the same characters since it was written; stripping
 * them here is what keeps the drawing and the notifying in agreement. The
 * stripped characters come back as TEXT, so the invariant above still holds.
 */
export function splitMentionParts(
  input: string,
  resolve: (handle: string) => MentionChipPerson | null,
): MentionPart[] {
  const source = String(input ?? '');
  if (!source) return [];
  if (source.indexOf('@') === -1) return [{ kind: 'text', text: source }];

  // ONE grammar. The regex is rebuilt per call rather than shared, because a
  // global regex carries `lastIndex` and a shared object turns two callers into
  // one intermittent bug.
  const mention = new RegExp(PLAIN_MENTION_SOURCE, 'g');
  const parts: MentionPart[] = [];
  let cursor = 0;
  let m: RegExpExecArray | null;

  while ((m = mention.exec(source)) !== null) {
    const captured = m[1] ?? '';
    // The leading boundary the grammar matched (start-of-string or one space)
    // belongs to the text before the mention, never to the mention.
    const at = m.index + (m[0].length - captured.length - 1);
    const handle = captured.replace(/[._]+$/, '');
    const person = handle ? resolve(handle) : null;
    const name = person ? String(person.name ?? '').trim() : '';
    if (!person || !name) continue; // left exactly as typed

    if (at > cursor) parts.push({ kind: 'text', text: source.slice(cursor, at) });
    parts.push({
      kind: 'mention',
      handle,
      raw: source.slice(at, at + handle.length + 1),
      person: { name, glyph: person.glyph },
    });
    cursor = at + handle.length + 1;
    // Resume from the end of the HANDLE, so stripped punctuation is re-offered
    // as the boundary of whatever follows it.
    mention.lastIndex = cursor;
  }

  if (parts.length === 0) return [{ kind: 'text', text: source }];
  if (cursor < source.length) parts.push({ kind: 'text', text: source.slice(cursor) });
  return parts;
}

/** Split html into tags and text. Only the text halves are ever transformed. */
const HTML_CHUNK = /<[^>]*>|[^<]+/g;

function escapeChipText(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Replace every `@handle` the resolver places with that person's chip.
 *
 * The chip's markup is correct AT THE PRODUCER: nothing validates it
 * afterwards, so the name and the glyph are escaped here.
 */
export function decorateMentionChips(
  html: string,
  resolve: (handle: string) => MentionChipPerson | null,
): string {
  const source = String(html ?? '');
  if (!source || source.indexOf('@') === -1) return source;

  let touched = false;
  const out = source.replace(HTML_CHUNK, (chunk) => {
    if (chunk.charCodeAt(0) === 60 /* '<' */) return chunk; // a tag: never text
    const parts = splitMentionParts(chunk, resolve);
    if (!parts.some((p) => p.kind === 'mention')) return chunk;
    touched = true;
    return parts
      .map((part) => {
        if (part.kind === 'text') return part.text;
        const name = escapeChipText(part.person.name);
        const glyph = escapeChipText(part.person.glyph || part.person.name.charAt(0)).slice(0, 2);
        return `<span class="${MENTION_CHIP_CLASS}">${mentionChipGlyphHtml(glyph)}${name}</span>`;
      })
      .join('');
  });

  return touched ? out : source;
}
